/**
 * A wave shooter, so the levels can be played rather than only inspected.
 *
 * The enemies path on the baked navmesh — the same surface the validator gates
 * reachability on. That is the point of the mode: if a level compiles clean and
 * the bake says every space is reachable, then a thing that only walks and
 * steps should be able to find you anywhere you can stand. Somewhere to hide
 * that they cannot reach is a level defect made visible at 60 fps.
 */

import * as THREE from 'three';

import { bakeNavmesh, type NavmeshGraph } from 'levelspec';
import type { CompiledLevel } from 'levelspec';
import { fromThree, toThree } from '../three/adapter.ts';
import type { Collider, Player } from '../three/player.ts';

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const WEAPON = {
  damage: 26,
  headMultiplier: 2.4,
  fireInterval: 0.11,
  magazine: 30,
  reloadTime: 1.6,
  range: 140,
  spread: 0.006,
};

const PLAYER = {
  health: 100,
  regenDelay: 4.0,
  regenRate: 9,
};

const ENEMY = {
  radius: 0.42,
  height: 1.75,
  /** Never faster than the player's 5.0 m/s walk — you can always kite. */
  maxSpeed: 4.5,
  attackRange: 2.2,
  attackInterval: 0.9,
  attackDamage: 9,
  separation: 0.85,
  /** They crowd you, but never walk into the camera. */
  standoff: 1.15,
};

export interface GameState {
  running: boolean;
  alive: boolean;
  wave: number;
  health: number;
  maxHealth: number;
  ammo: number;
  magazine: number;
  reloading: boolean;
  reloadProgress: number;
  score: number;
  kills: number;
  headshots: number;
  enemiesAlive: number;
  enemiesRemainingInWave: number;
  intermission: number;
  message: string;
  hitFlash: number;
  damageFlash: number;
  navmeshSamples: number;
  /** Enemies that could not find a path to the player this frame. */
  stuck: number;
}

interface Enemy {
  group: THREE.Group;
  body: THREE.Mesh;
  head: THREE.Mesh;
  /** Feet position, Three.js frame. */
  pos: THREE.Vector3;
  health: number;
  maxHealth: number;
  speed: number;
  node: number;
  attackCooldown: number;
  hitFlash: number;
  bob: number;
  dead: boolean;
}

// ---------------------------------------------------------------------------

export class Game {
  readonly state: GameState = {
    running: false,
    alive: true,
    wave: 0,
    health: PLAYER.health,
    maxHealth: PLAYER.health,
    ammo: WEAPON.magazine,
    magazine: WEAPON.magazine,
    reloading: false,
    reloadProgress: 0,
    score: 0,
    kills: 0,
    headshots: 0,
    enemiesAlive: 0,
    enemiesRemainingInWave: 0,
    intermission: 0,
    message: '',
    hitFlash: 0,
    damageFlash: 0,
    navmeshSamples: 0,
    stuck: 0,
  };

  private scene: THREE.Scene;
  private player: Player;
  private graph: NavmeshGraph | null = null;
  private colliders: Collider[] = [];

  private enemies: Enemy[] = [];
  private pool: Enemy[] = [];
  private root = new THREE.Group();
  private tracers = new THREE.Group();

  /** BFS distance from the player over the navmesh: the flow field enemies descend. */
  private dist: Int32Array = new Int32Array(0);
  private queue: Int32Array = new Int32Array(0);
  private flowTimer = 0;
  private playerNode = -1;

  private spawnQueue = 0;
  private spawnFailures = 0;
  private killsAtWaveStart = 0;
  private spawnTimer = 0;
  private fireTimer = 0;
  private reloadTimer = 0;
  private sinceDamage = 0;
  private triggerHeld = false;

  private muzzle: THREE.PointLight;

  constructor(scene: THREE.Scene, player: Player) {
    this.scene = scene;
    this.player = player;
    this.muzzle = new THREE.PointLight(0xffd9a0, 0, 12, 2);
    this.root.add(this.tracers);
    this.root.add(this.muzzle);
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /** Bake the navmesh for this level and reset. Returns false if unplayable. */
  start(level: CompiledLevel, colliders: Collider[], spawnAt: THREE.Vector3): boolean {
    this.stop();
    this.colliders = colliders;

    const mesh = bakeNavmesh(level, { keepGraph: true });
    if (!mesh.graph || mesh.graph.count === 0) return false;
    this.graph = mesh.graph;
    this.state.navmeshSamples = mesh.graph.count;
    this.dist = new Int32Array(mesh.graph.count);
    this.queue = new Int32Array(mesh.graph.count);

    this.scene.add(this.root);
    this.player.mode = 'walk';
    this.player.teleport(spawnAt);

    Object.assign(this.state, {
      running: true,
      alive: true,
      wave: 0,
      health: PLAYER.health,
      ammo: WEAPON.magazine,
      reloading: false,
      reloadProgress: 0,
      score: 0,
      kills: 0,
      headshots: 0,
      enemiesAlive: 0,
      enemiesRemainingInWave: 0,
      intermission: 3.0,
      message: 'Wave 1 incoming',
      hitFlash: 0,
      damageFlash: 0,
      stuck: 0,
    });
    this.spawnQueue = 0;
    this.spawnFailures = 0;
    this.killsAtWaveStart = 0;
    this.fireTimer = 0;
    this.reloadTimer = 0;
    this.sinceDamage = 99;
    this.flowTimer = 0;
    this.playerNode = -1;
    return true;
  }

  stop(): void {
    for (const e of this.enemies) this.retire(e);
    this.enemies.length = 0;
    this.tracers.clear();
    this.scene.remove(this.root);
    this.state.running = false;
    this.triggerHeld = false;
  }

  setTrigger(down: boolean): void {
    this.triggerHeld = down;
    if (down) this.fireTimer = Math.min(this.fireTimer, 0);
  }

  reload(): void {
    if (this.state.reloading || this.state.ammo === WEAPON.magazine) return;
    this.state.reloading = true;
    this.reloadTimer = WEAPON.reloadTime;
  }

  // -------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------

  update(dt: number): void {
    if (!this.state.running || !this.graph) return;
    const s = this.state;
    s.hitFlash = Math.max(0, s.hitFlash - dt * 3);
    s.damageFlash = Math.max(0, s.damageFlash - dt * 1.6);
    this.muzzle.intensity = Math.max(0, this.muzzle.intensity - dt * 60);
    this.fadeTracers(dt);

    if (!s.alive) return;

    // Weapon
    if (s.reloading) {
      this.reloadTimer -= dt;
      s.reloadProgress = 1 - Math.max(0, this.reloadTimer) / WEAPON.reloadTime;
      if (this.reloadTimer <= 0) {
        s.reloading = false;
        s.ammo = WEAPON.magazine;
      }
    }
    this.fireTimer -= dt;
    if (this.triggerHeld && !s.reloading && this.fireTimer <= 0) {
      if (s.ammo > 0) {
        this.fire();
        this.fireTimer = WEAPON.fireInterval;
      } else {
        this.reload();
      }
    }

    // Health
    this.sinceDamage += dt;
    if (this.sinceDamage > PLAYER.regenDelay && s.health < s.maxHealth) {
      s.health = Math.min(s.maxHealth, s.health + PLAYER.regenRate * dt);
    }

    // Waves
    if (s.intermission > 0) {
      s.intermission -= dt;
      if (s.intermission <= 0) this.beginWave(s.wave + 1);
    }
    if (this.spawnQueue > 0) {
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0 && this.enemies.length < 34) {
        if (this.spawnEnemy()) {
          this.spawnQueue--;
          this.spawnFailures = 0;
        } else if (++this.spawnFailures > 12) {
          // Nowhere on the mesh to put them: say so instead of silently
          // clearing waves against an empty room.
          this.spawnQueue = 0;
          s.message = 'No reachable space to spawn enemies';
        }
        this.spawnTimer = Math.max(0.14, 0.75 - s.wave * 0.03);
      }
    }

    this.updateFlowField(dt);
    this.updateEnemies(dt);

    s.enemiesAlive = this.enemies.length;
    s.enemiesRemainingInWave = this.spawnQueue + this.enemies.length;
    if (s.wave > 0 && s.intermission <= 0 && this.spawnQueue === 0 && this.enemies.length === 0 && s.kills > this.killsAtWaveStart) {
      s.intermission = 5.0;
      s.message = `Wave ${s.wave} cleared`;
      s.score += 50 * s.wave;
    }
  }

  // -------------------------------------------------------------------------
  // Waves
  // -------------------------------------------------------------------------

  private beginWave(n: number): void {
    const s = this.state;
    s.wave = n;
    this.killsAtWaveStart = s.kills;
    this.spawnQueue = 4 + Math.round(n * 2.2);
    this.spawnTimer = 0;
    s.message = `Wave ${n}`;
  }

  private enemyStats(): { health: number; speed: number } {
    const w = this.state.wave;
    return {
      health: 24 + (w - 1) * 11,
      speed: Math.min(ENEMY.maxSpeed, 2.0 + (w - 1) * 0.14),
    };
  }

  /**
   * Spawn on a reachable sample, far enough away and preferably out of sight.
   * The distance band relaxes if the reachable region is too small to satisfy
   * it — a cramped level should still be playable, just claustrophobic.
   */
  private spawnEnemy(): boolean {
    const g = this.graph!;
    if (this.playerNode < 0) return false;
    const bands: [number, number][] = [
      [14, 75],
      [7, 120],
      [3, Infinity],
    ];
    let unseen: number[] = [];
    let any: number[] = [];
    for (const [lo, hi] of bands) {
      unseen = [];
      any = [];
      for (let i = 0; i < g.count; i += 7) {
        if (!g.reachable[i]) continue;
        const d = this.dist[i];
        if (d <= 0) continue;
        // BFS hops are one navmesh cell each: ~0.25 m.
        const meters = d * 0.25;
        if (meters < lo || meters > hi) continue;
        any.push(i);
        const p = toThree(g.points[i * 3], g.points[i * 3 + 1], g.points[i * 3 + 2]);
        if (!this.canSee(p.clone().setY(p.y + 1.2), this.player.camera.position)) unseen.push(i);
      }
      if (any.length) break;
    }
    const source = unseen.length ? unseen : any;
    if (!source.length) return false;
    const pick = source[Math.floor(Math.random() * source.length)];

    const stats = this.enemyStats();
    const e = this.acquire();
    const p = toThree(g.points[pick * 3], g.points[pick * 3 + 1], g.points[pick * 3 + 2]);
    e.pos.copy(p);
    e.node = pick;
    e.health = stats.health;
    e.maxHealth = stats.health;
    e.speed = stats.speed;
    e.attackCooldown = 0;
    e.hitFlash = 0;
    e.dead = false;
    e.bob = Math.random() * Math.PI * 2;
    e.group.visible = true;
    e.group.position.copy(e.pos);
    this.enemies.push(e);
    return true;
  }

  // -------------------------------------------------------------------------
  // Flow field
  // -------------------------------------------------------------------------

  /**
   * BFS outward from the player's navmesh sample. Every enemy then just walks
   * to whichever neighbour has a lower hop count, which is both cheap and
   * exactly as connected as the bake says the level is.
   */
  private updateFlowField(dt: number): void {
    this.flowTimer -= dt;
    const g = this.graph!;
    const feet = this.player.pos;
    const canon = fromThree(new THREE.Vector3(feet.x, feet.y, feet.z));
    const node = g.locate(canon[0], canon[1], canon[2] + 0.1, 4);
    if (node < 0) return;
    if (this.flowTimer > 0 && node === this.playerNode) return;
    this.flowTimer = 0.2;
    this.playerNode = node;

    this.dist.fill(-1);
    const q = this.queue;
    let head = 0;
    let tail = 0;
    q[tail++] = node;
    this.dist[node] = 0;
    while (head < tail) {
      const u = q[head++];
      const du = this.dist[u] + 1;
      for (let k = g.adjOffset[u]; k < g.adjOffset[u + 1]; k++) {
        const v = g.adjList[k];
        if (this.dist[v] !== -1) continue;
        this.dist[v] = du;
        q[tail++] = v;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Enemies
  // -------------------------------------------------------------------------

  private updateEnemies(dt: number): void {
    const g = this.graph!;
    const cam = this.player.camera.position;
    let stuck = 0;

    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      e.hitFlash = Math.max(0, e.hitFlash - dt * 4);
      e.bob += dt * e.speed * 2.4;

      // Re-anchor to whatever sample is under the enemy right now. Walking a
      // stored node forward is fragile; asking the mesh where you are is not.
      const canon = fromThree(e.pos);
      const here = g.locate(canon[0], canon[1], canon[2] + 0.1, 3);
      if (here >= 0) e.node = here;
      else if (e.node < 0 || this.dist[e.node] === -1) e.node = g.locate(canon[0], canon[1], canon[2] + 0.1, 6);

      const toPlayer = new THREE.Vector3(cam.x - e.pos.x, 0, cam.z - e.pos.z);
      const planar = toPlayer.length();

      let desired: THREE.Vector3 | null = null;
      if (planar < 2.6 && Math.abs(cam.y - 1.6 - e.pos.y) < 1.5) {
        // Close enough to make straight for the player.
        desired = toPlayer.clone().normalize();
      } else if (e.node >= 0 && this.dist[e.node] > 0) {
        // Descend the flow field a few nodes ahead and steer at that, so the
        // path reads as a walk rather than a 25 cm staircase.
        let cur = e.node;
        let hops = 0;
        while (hops < 10) {
          let best = -1;
          let bestD = this.dist[cur];
          for (let k = g.adjOffset[cur]; k < g.adjOffset[cur + 1]; k++) {
            const v = g.adjList[k];
            const d = this.dist[v];
            if (d !== -1 && d < bestD) {
              bestD = d;
              best = v;
            }
          }
          if (best < 0) break;
          cur = best;
          hops++;
        }
        if (hops === 0) {
          stuck++;
        } else {
          const target = toThree(g.points[cur * 3], g.points[cur * 3 + 1], g.points[cur * 3 + 2]);
          const dx = target.x - e.pos.x;
          const dz = target.z - e.pos.z;
          const len = Math.hypot(dx, dz);
          if (len > 1e-5) desired = new THREE.Vector3(dx / len, 0, dz / len);
          // Height comes from the ground under our feet, not the lookahead,
          // so stairs are climbed a tread at a time.
          const ground = e.node >= 0 ? g.points[e.node * 3 + 2] : e.pos.y;
          e.pos.y += Math.max(-8 * dt, Math.min(8 * dt, ground - e.pos.y));
        }
      } else {
        stuck++;
      }

      if (desired) {
        // Keep a little personal space so a wave does not become one blob —
        // but let it collapse as they close, or the crowd holds itself just
        // outside its own reach and nobody ever lands a hit.
        const crowding = planar > ENEMY.attackRange ? 0.55 : 0.0;
        if (crowding > 0) {
          for (const o of this.enemies) {
            if (o === e) continue;
            const dx = e.pos.x - o.pos.x;
            const dz = e.pos.z - o.pos.z;
            const d2 = dx * dx + dz * dz;
            if (d2 > 1e-6 && d2 < ENEMY.separation * ENEMY.separation) {
              const d = Math.sqrt(d2);
              desired.x += (dx / d) * crowding;
              desired.z += (dz / d) * crowding;
            }
          }
        }
        desired.normalize();
        // Hold a body's width off the player: close enough to swing at you,
        // never so close that they are rendering from inside your head.
        if (planar < ENEMY.standoff) {
          const inward = (desired.x * toPlayer.x + desired.z * toPlayer.z) / (planar || 1);
          if (inward > 0) {
            desired.x -= (toPlayer.x / planar) * inward;
            desired.z -= (toPlayer.z / planar) * inward;
            if (desired.lengthSq() < 1e-6) desired.set(0, 0, 0);
            else desired.normalize();
          }
        }
        e.pos.x += desired.x * e.speed * dt;
        e.pos.z += desired.z * e.speed * dt;
        if (desired.lengthSq() > 1e-6) e.group.rotation.y = Math.atan2(desired.x, desired.z);
      }

      e.group.position.set(e.pos.x, e.pos.y + Math.sin(e.bob) * 0.045, e.pos.z);
      const flash = e.hitFlash;
      (e.body.material as THREE.MeshStandardMaterial).emissiveIntensity = flash;
      (e.head.material as THREE.MeshStandardMaterial).emissiveIntensity = flash;

      // Attack
      e.attackCooldown -= dt;
      const eye = new THREE.Vector3(e.pos.x, e.pos.y + 1.5, e.pos.z);
      if (
        planar < ENEMY.attackRange &&
        Math.abs(cam.y - eye.y) < 2.0 &&
        e.attackCooldown <= 0 &&
        this.state.alive
      ) {
        e.attackCooldown = ENEMY.attackInterval;
        this.damagePlayer(ENEMY.attackDamage);
      }
    }
    this.state.stuck = stuck;
  }

  private damagePlayer(amount: number): void {
    const s = this.state;
    s.health -= amount;
    s.damageFlash = 1;
    this.sinceDamage = 0;
    if (s.health <= 0) {
      s.health = 0;
      s.alive = false;
      s.message = `You died on wave ${s.wave}`;
      this.triggerHeld = false;
    }
  }

  // -------------------------------------------------------------------------
  // Shooting
  // -------------------------------------------------------------------------

  private fire(): void {
    const s = this.state;
    s.ammo--;
    const cam = this.player.camera;
    const origin = cam.position.clone();
    const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    dir.x += (Math.random() - 0.5) * WEAPON.spread;
    dir.y += (Math.random() - 0.5) * WEAPON.spread;
    dir.z += (Math.random() - 0.5) * WEAPON.spread;
    dir.normalize();

    this.muzzle.position.copy(origin).addScaledVector(dir, 0.4);
    this.muzzle.intensity = 6;

    const wall = this.raycastWorld(origin, dir, WEAPON.range);
    let hit: { enemy: Enemy; t: number; head: boolean } | null = null;
    for (const e of this.enemies) {
      const r = this.raycastEnemy(origin, dir, e);
      if (!r) continue;
      if (r.t > wall) continue;
      if (!hit || r.t < hit.t) hit = { enemy: e, t: r.t, head: r.head };
    }

    const end = origin.clone().addScaledVector(dir, hit ? hit.t : Math.min(wall, WEAPON.range));
    this.addTracer(origin.clone().addScaledVector(dir, 0.5), end);

    if (!hit) return;
    const dmg = WEAPON.damage * (hit.head ? WEAPON.headMultiplier : 1);
    hit.enemy.health -= dmg;
    hit.enemy.hitFlash = 1;
    s.hitFlash = 1;
    if (hit.enemy.health <= 0) {
      s.kills++;
      if (hit.head) s.headshots++;
      s.score += hit.head ? 25 : 10;
      this.kill(hit.enemy);
    }
  }

  private kill(e: Enemy): void {
    const i = this.enemies.indexOf(e);
    if (i >= 0) this.enemies.splice(i, 1);
    this.retire(e);
  }

  /** Distance to the nearest world solid along the ray, or `range`. */
  private raycastWorld(origin: THREE.Vector3, dir: THREE.Vector3, range: number): number {
    let best = range;
    const inv = new THREE.Vector3(1 / dir.x, 1 / dir.y, 1 / dir.z);
    for (const c of this.colliders) {
      const b = c.box;
      let t0 = (b.min.x - origin.x) * inv.x;
      let t1 = (b.max.x - origin.x) * inv.x;
      if (t0 > t1) [t0, t1] = [t1, t0];
      let u0 = (b.min.y - origin.y) * inv.y;
      let u1 = (b.max.y - origin.y) * inv.y;
      if (u0 > u1) [u0, u1] = [u1, u0];
      let v0 = (b.min.z - origin.z) * inv.z;
      let v1 = (b.max.z - origin.z) * inv.z;
      if (v0 > v1) [v0, v1] = [v1, v0];
      const near = Math.max(t0, u0, v0);
      const farT = Math.min(t1, u1, v1);
      if (near > farT || farT < 0) continue;
      const t = near < 0 ? farT : near;
      if (t < best) best = t;
    }
    return best;
  }

  private raycastEnemy(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    e: Enemy,
  ): { t: number; head: boolean } | null {
    const r = ENEMY.radius;
    const minX = e.pos.x - r;
    const maxX = e.pos.x + r;
    const minZ = e.pos.z - r;
    const maxZ = e.pos.z + r;
    const minY = e.pos.y;
    const maxY = e.pos.y + ENEMY.height;
    let t0 = (minX - origin.x) / dir.x;
    let t1 = (maxX - origin.x) / dir.x;
    if (t0 > t1) [t0, t1] = [t1, t0];
    let u0 = (minY - origin.y) / dir.y;
    let u1 = (maxY - origin.y) / dir.y;
    if (u0 > u1) [u0, u1] = [u1, u0];
    let v0 = (minZ - origin.z) / dir.z;
    let v1 = (maxZ - origin.z) / dir.z;
    if (v0 > v1) [v0, v1] = [v1, v0];
    const near = Math.max(t0, u0, v0);
    const farT = Math.min(t1, u1, v1);
    if (near > farT || farT < 0) return null;
    const t = near < 0 ? farT : near;
    const hitY = origin.y + dir.y * t;
    return { t, head: hitY > e.pos.y + ENEMY.height - 0.38 };
  }

  private canSee(from: THREE.Vector3, to: THREE.Vector3): boolean {
    const dir = to.clone().sub(from);
    const len = dir.length();
    if (len < 1e-4) return true;
    dir.divideScalar(len);
    return this.raycastWorld(from, dir, len) >= len - 1e-3;
  }

  // -------------------------------------------------------------------------
  // Visuals
  // -------------------------------------------------------------------------

  private acquire(): Enemy {
    const reused = this.pool.pop();
    if (reused) {
      this.root.add(reused.group);
      return reused;
    }
    const group = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(ENEMY.radius * 2, ENEMY.height - 0.42, ENEMY.radius * 2),
      new THREE.MeshStandardMaterial({ color: 0xb3372f, emissive: 0xff8a5c, emissiveIntensity: 0, roughness: 0.7 }),
    );
    body.position.y = (ENEMY.height - 0.42) / 2;
    const head = new THREE.Mesh(
      new THREE.BoxGeometry(0.34, 0.34, 0.34),
      new THREE.MeshStandardMaterial({ color: 0xe6a06a, emissive: 0xff8a5c, emissiveIntensity: 0, roughness: 0.6 }),
    );
    head.position.y = ENEMY.height - 0.2;
    group.add(body, head);
    this.root.add(group);
    return {
      group,
      body,
      head,
      pos: new THREE.Vector3(),
      health: 1,
      maxHealth: 1,
      speed: 2,
      node: -1,
      attackCooldown: 0,
      hitFlash: 0,
      bob: 0,
      dead: false,
    };
  }

  private retire(e: Enemy): void {
    e.group.visible = false;
    this.root.remove(e.group);
    this.pool.push(e);
  }

  private addTracer(a: THREE.Vector3, b: THREE.Vector3): void {
    const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
    const line = new THREE.Line(
      geo,
      new THREE.LineBasicMaterial({ color: 0xffe9b0, transparent: true, opacity: 0.85, depthTest: false }),
    );
    line.renderOrder = 7;
    line.userData.life = 0.07;
    this.tracers.add(line);
  }

  private fadeTracers(dt: number): void {
    for (let i = this.tracers.children.length - 1; i >= 0; i--) {
      const l = this.tracers.children[i] as THREE.Line;
      l.userData.life -= dt;
      const m = l.material as THREE.LineBasicMaterial;
      m.opacity = Math.max(0, l.userData.life / 0.07) * 0.85;
      if (l.userData.life <= 0) {
        l.geometry.dispose();
        m.dispose();
        this.tracers.remove(l);
      }
    }
  }
}
